import React, { useState } from 'react';
import { Box } from '@mui/material';

import { useLoadMonitor } from '@paragon/client-tools';
import { Lumber } from '@paragon/contract-library';
import {
  applyLumberPatch,
  getLoggedInUsersOrganizationMaterialSet,
  LumberSelects,
  MaterialListSelect,
} from '@paragon/material-lists';

import { getUniqueLumbersInLumberList } from '../../store/selectors/getUniqueLumbersInLumberList';
import { useAppSelector } from '../hooks';

interface Props {
  readonly value: Lumber | null;
  readonly onChange: (value: Lumber) => void;
}

export function MaterialListLumberPicker(props: Props) {
  const [lumberListGuid, setLumberListGuid] = useState<string | null>(null);

  const materialSet = useAppSelector(getLoggedInUsersOrganizationMaterialSet);
  const lumberInActiveInventorySet = lumberListGuid ? getUniqueLumbersInLumberList(materialSet, lumberListGuid) : null;

  useLoadMonitor(() => {
    if (!materialSet) {
      return false;
    }

    setLumberListGuid(Object.keys(materialSet.lumberLists)[0]);
  });

  useLoadMonitor(() => {
    if (!lumberInActiveInventorySet || lumberInActiveInventorySet.length === 0) {
      return false;
    }

    props.onChange(lumberInActiveInventorySet[0]);
  });

  // Because of the progressive loading involved in this component (see useLoadMonitor usages above), it may take up to
  //   two renders before this component's initial state is fully resolved.  No network communication is involved, so
  //   this delay should be imperceptible.  To prevent a jarring loading screen flash, we just avoid rendering until all
  //   the data we care about is loaded.
  if (!lumberListGuid || !materialSet || !props.value || !lumberInActiveInventorySet) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <MaterialListSelect
        fullWidth={false}
        sx={{ m: 1 }}
        type="lumber"
        selectedGuid={lumberListGuid}
        onEdit={setLumberListGuid}
      />
      <Box
        sx={{
          margin: 1,

          display: 'grid',
          gap: 2,
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',

          '& > *': {
            margin: 0,
            width: 'unset',
          },
        }}
      >
        <LumberSelects
          strict
          inputSx={{ m: 1, flex: 1 }}
          alwaysValid
          value={props.value}
          onChange={(patch) => {
            props.onChange(applyLumberPatch(props.value!, patch, lumberInActiveInventorySet));
          }}
          lumbers={lumberInActiveInventorySet}
        />
      </Box>
    </Box>
  );
}
